// Package imports:
import React, { useMemo } from 'react';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import DelayOrClosedLabel from '../DelayOrClosedLabel/DelayOrClosedLabel';
import Alert from '../../ui-elements/Alert/Alert';
import Loading from '../../ui-elements/Loading/Loading';
import TandemTable from '../../ui-elements/Table/TandemTable';
import ColoredNumber from '../../ui-elements/ColoredNumber/ColoredNumber';
import Link from '../../ui-elements/Link/Link';
// Service imports:
import { convertToPercentage, formatNumber, displayQuantityShorthand } from '../../services/utils';
// Type imports:
import { IApiLmdSnapshot } from '../../types/HlutabrefTypes';
import { IDefaultProps } from '../../types/Types';
import { useApiLmdData } from '../../services/apiHooks';

type OverviewType = 'shares' | 'bonds';

interface IOwnProps {
    overviewType?: OverviewType
}
type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const MarketOverview: React.FC<Props> = ({
    overviewType = 'shares',
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const [overviewData] = useApiLmdData<IApiLmdSnapshot[]>(
        (overviewType === 'shares'
            ? `/v1/market_data/v1/keldan/exchanges/[XICE;FNIS]/equities/delayed_snapshot`
            : `/v1/market_data/v1/exchanges/XICE/markets/*/bonds/*/delayed_snapshot`
        ),
        accessToken,
        refreshRateMs
    );

    const mostIncrease = useMemo(() => {
        const { data } = overviewData;
        if (data === null) return [];
        const increased = data.filter((d) => d.intraday_per_change !== null && d.intraday_per_change > 0).sort((a, b) => {
            return a.intraday_per_change === null && b.intraday_per_change === null
            ? 0
            : a.intraday_per_change === null
            ? 1
            : b.intraday_per_change === null
            ? -1
            : a.intraday_per_change < b.intraday_per_change
            ? 1
            : a.intraday_per_change > b.intraday_per_change
            ? -1
            : 0;
        });
        return increased.slice(0, 3);
    }, [overviewData]);

    const mostDecrease = useMemo(() => {
        const { data } = overviewData;
        if (data === null) return [];
        const decreased = data.filter((d) => d.intraday_per_change !== null && d.intraday_per_change < 0).sort((a, b) => {
            return a.intraday_per_change === null && b.intraday_per_change === null
            ? 0
            : a.intraday_per_change === null
            ? -1
            : b.intraday_per_change === null
            ? 1
            : a.intraday_per_change < b.intraday_per_change
            ? -1
            : a.intraday_per_change > b.intraday_per_change
            ? 1
            : 0;
        });
        return decreased.slice(0, 3);
    }, [overviewData]);

    const mostTurnover = useMemo(() => {
        const { data } = overviewData;
        if (data === null) return [];
        const turnover = data.filter((d) => d.intraday_accumulated_turnover !== null && d.intraday_accumulated_turnover > 0).sort((a, b) => {
            return a.intraday_accumulated_turnover === null && b.intraday_accumulated_turnover === null
            ? 0
            : a.intraday_accumulated_turnover === null
            ? 1
            : b.intraday_accumulated_turnover === null
            ? -1
            : a.intraday_accumulated_turnover < b.intraday_accumulated_turnover
            ? 1
            : a.intraday_accumulated_turnover > b.intraday_accumulated_turnover
            ? -1
            : 0;
        });
        return turnover.slice(0, 3);
    }, [overviewData]);

    const totals = useMemo(() => {
        const { data } = overviewData;
        if (data === null) return [];
        let totalTurnover = 0;
        let totalTrades = 0;
        for (let d of data) {
            totalTurnover += d.intraday_accumulated_turnover ?? 0;
            totalTrades += d.intraday_no_of_trades ?? 0;
        }
        return [{
            symbol: '',
            intraday_accumulated_turnover: totalTurnover,
            intraday_no_of_trades: totalTrades,
            market_symbol: null, bid: null, ask: null, last_price: null, last_price_diff: null, previous_closing_price: null, intraday_accumulated_volume: null,intraday_per_change: null, trading_start: null, trading_close: null, last_yield: null
        }];
    }, [overviewData]);

    const noIncrease: IApiLmdSnapshot[] = [{symbol: 'Engin hækkun hefur orðið í dag', intraday_per_change: null, last_price: null, intraday_accumulated_turnover: null, intraday_no_of_trades: null, market_symbol: null, bid: null, ask: null, last_price_diff: null, previous_closing_price: null, intraday_accumulated_volume: null, trading_start: null, trading_close: null, last_yield: null}];
    const noDecrease: IApiLmdSnapshot[] = [{symbol: 'Engin lækkun hefur orðið í dag', intraday_per_change: null, last_price: null, intraday_accumulated_turnover: null, intraday_no_of_trades: null, market_symbol: null, bid: null, ask: null, last_price_diff: null, previous_closing_price: null, intraday_accumulated_volume: null, trading_start: null, trading_close: null, last_yield: null}];
    const noTurnover: IApiLmdSnapshot[] = [{symbol: 'Engin viðskipti hafa orðið í dag', intraday_accumulated_turnover: null, intraday_no_of_trades: null, intraday_per_change: null, last_price: null, market_symbol: null, bid: null, ask: null, last_price_diff: null, previous_closing_price: null, intraday_accumulated_volume: null, trading_start: null, trading_close: null, last_yield: null}];

    return (
        <DisplayBox className='KCL_market-overview'
            title="Helstu tölur í dag"
            statusComponent={<DelayOrClosedLabel closeType={overviewType} />}
            {...props}
        >
            {(overviewData.data === null)
            ? <Loading />
            : (overviewData.error instanceof Error)
            ? <Alert type={'error'} headText={overviewData.error.message}/>
            : (
                <TandemTable
                    data={[
                        mostIncrease.length > 0 ? mostIncrease : noIncrease,
                        mostDecrease.length > 0 ? mostDecrease : noDecrease,
                        mostTurnover.length > 0 ? mostTurnover : noTurnover,
                        totals
                    ]}
                    columns={[
                        mostIncrease.length > 0
                            ? [{
                                title: 'Mesta hækkun',
                                dataAccessor: ({symbol}) => (
                                    <Link url={`/Markadir/${(overviewType === 'shares') ? 'Hlutabref' : 'Skuldabref' }/${symbol}`} linkSize='15'>{symbol}</Link>
                                ),
                                textAlign: 'left'
                            }, {
                                title: 'Breyting',
                                dataAccessor: ({intraday_per_change}) => {
                                    return (
                                        <ColoredNumber
                                            hasArrow={intraday_per_change !== null}
                                            sign={intraday_per_change !== null ? 'pos' : 'neutral'}
                                        >
                                            {convertToPercentage(intraday_per_change, true)}
                                        </ColoredNumber>
                                    )
                                },
                            }, {
                                title: 'Síðasta söluverð',
                                dataAccessor: ({last_price, last_yield}) => formatNumber(overviewType === 'shares' ? last_price : last_yield),
                                textAlign: 'right'
                            }]
                            : [{
                                title: 'Mesta hækkun',
                                dataAccessor: ({symbol}) => symbol,
                                textAlign: 'left'
                            }, {
                                title: 'Breyting',
                                dataAccessor: ({intraday_per_change}) => intraday_per_change,
                                textAlign: 'right'
                            }, {
                                title: 'Síðasta söluverð',
                                dataAccessor: ({last_price}) => last_price
                            }],
                        mostDecrease.length > 0
                            ? [{
                                title: 'Mesta lækkun',
                                dataAccessor: ({symbol}) => (
                                    <Link url={`/Markadir/${(overviewType === 'shares') ? 'Hlutabref' : 'Skuldabref' }/${symbol}`} linkSize='15'>{symbol}</Link>
                                ),
                                textAlign: 'left'
                            }, {
                                title: 'Breyting',
                                dataAccessor: ({intraday_per_change}) => {
                                    return (
                                        <ColoredNumber
                                            hasArrow={intraday_per_change !== null}
                                            sign={intraday_per_change !== null ? 'neg' : 'neutral'}
                                        >
                                            {convertToPercentage(intraday_per_change, true)}
                                        </ColoredNumber>
                                    )
                                }
                            }, {
                                title: 'Síðasta söluverð',
                                dataAccessor: ({last_price, last_yield}) => formatNumber(overviewType === 'shares' ? last_price : last_yield),
                                textAlign: 'right'
                            }]
                            : [{
                                title: 'Mesta lækkun',
                                dataAccessor: ({symbol}) => symbol,
                                textAlign: 'left'
                            }, {
                                title: 'Breyting',
                                dataAccessor: ({intraday_per_change}) => intraday_per_change,
                                textAlign: 'right'
                            }, {
                                title: 'Síðasta söluverð',
                                dataAccessor: ({last_price}) => last_price
                            }],
                        mostTurnover.length > 0
                            ? [{
                                title: 'Mesta velta',
                                dataAccessor: ({symbol}) => (
                                    symbol !== 'Samtals'
                                    ? <Link url={`/Markadir/${(overviewType === 'shares') ? 'Hlutabref' : 'Skuldabref' }/${symbol}`} linkSize='15'>{symbol}</Link>
                                    : <strong>{symbol}</strong>
                                ),
                                textAlign: 'left'
                            }, {
                                title: 'Velta',
                                dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                                textAlign: 'right'
                            }, {
                                title: 'Fjöldi viðskipta',
                                dataAccessor: ({intraday_no_of_trades}) => intraday_no_of_trades,
                                textAlign: 'right'
                            }]
                            : [{
                                title: 'Mesta velta',
                                dataAccessor: ({symbol}) => symbol,
                                textAlign: 'left'
                            }, {
                                title: 'Velta',
                                dataAccessor: ({intraday_accumulated_turnover}) => intraday_accumulated_turnover,
                                textAlign: 'right'
                            }, {
                                title: 'Fjöldi viðskipta',
                                dataAccessor: ({intraday_no_of_trades}) => intraday_no_of_trades
                            }],
                        [{
                            title: 'Heildarvelta',
                            dataAccessor: () => null,
                            textAlign: 'left'
                        }, {
                            title: 'Velta',
                            dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover)
                        }, {
                            title: 'Fjöldi',
                            dataAccessor: ({intraday_no_of_trades}) => intraday_no_of_trades
                        }]
                    ]}
                    tableSize="lg"
                />
            )}
        </DisplayBox>
    );
}

export default MarketOverview;